import { useEffect, useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Checkbox } from '../../../../../components/ui/Checkbox';
import { InputNumber } from '../../../../../components/ui/InputNumber';
import { Radio } from '../../../../../components/ui/Radio';
import { Select } from '../../../../../components/ui/Select';
import { Switch } from '../../../../../components/ui/Switch';
import { Tabs } from '../../../../../components/ui/Tabs';
import type { Coupon, CouponGroup } from '../fixtures';
import { relatedCoupons } from '../stackRules';
import { StackRangeField } from '../../../../shared/StackRangeField';
import styles from './CouponManagementPage.module.css';

interface CouponEditorProps {
  coupon: Coupon;
  entities: readonly Coupon[];
  groups: readonly CouponGroup[];
  isNew?: boolean;
  onBack: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onOpenRelation: (id: number) => void;
  onOpenRules: (id: number) => void;
  onSave: (next: 'back' | 'rules') => void;
}

const applyOptions = ['课程', '物品', '特殊物品', '商城套餐'] as const;
const centerOptions = [{ label: '刘志的体验校区', value: 'liuzhi' }];
const unlimitedOptions = [{ label: '不限', value: 'unlimited' }];

export function CouponEditor({ coupon, entities, groups, isNew = false, onBack, onDirtyChange, onOpenRelation, onOpenRules, onSave }: CouponEditorProps) {
  const [formTab, setFormTab] = useState<'basic' | 'condition'>('condition');
  const [thresholdMode, setThresholdMode] = useState<'none' | 'amount'>('none');
  const [applyTo, setApplyTo] = useState<readonly string[]>(['课程']);
  const [refundReturn, setRefundReturn] = useState(true);
  const [signupScopes, setSignupScopes] = useState<string[]>(['前台报名', '线上报名']);

  /* 预览卡里的汇总口径与下方的范围块同源，都取自同一份关系计算。 */
  const related = relatedCoupons(coupon.id, entities, groups);
  const rangeText = related.length > 0 ? `可与${related.length}张优惠券叠加使用` : '不可与其他优惠券叠加使用';
  const dirty = thresholdMode !== 'none'
    || applyTo.length !== 1 || !applyTo.includes('课程')
    || !refundReturn
    || signupScopes.length !== 2 || !signupScopes.includes('前台报名') || !signupScopes.includes('线上报名');

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  function toggleApply(item: string, checked: boolean) {
    setApplyTo((items) => checked ? [...items, item] : items.filter((value) => value !== item));
  }

  return <div className={styles.editor}>
    <div className={styles.editorLayout}>
      <section className={styles.previewColumn}>
        <h2 className={styles.previewTitle}>模拟效果</h2>
        <div className={styles.phone}>
          <div className={styles.phoneHead}><span aria-hidden="true">‹</span><span>优惠券</span><span aria-hidden="true">•••</span></div>
          <div className={styles.couponCard}>
            <div className={styles.couponTop}>
              <div aria-hidden="true" className={styles.gift}>¥</div>
              <div className={styles.couponSummary}><div className={styles.couponName}>{coupon.name}</div><p>领券当日起100天内可用</p></div>
              <div className={styles.couponAmount}><b>¥ {coupon.amount}</b><span>无门槛</span></div>
            </div>
            <dl className={styles.couponDetails}>
              <div><dt>优惠券名称：</dt><dd>{coupon.name}</dd></div>
              <div><dt>使用范围：</dt><dd>前台报名、线上报名</dd></div>
              <div><dt>使用门槛：</dt><dd>无门槛</dd></div>
              <div><dt>叠加范围：</dt><dd>{rangeText}</dd></div>
              <div><dt>适用于：</dt><dd>-中心：刘志的体验校区</dd></div>
              <div><dt /><dd>-课程：所有课程</dd></div>
              <div><dt /><dd>-学员类别：所有学员类别</dd></div>
            </dl>
          </div>
        </div>
      </section>

      <section className={styles.formPanel}>
        <Tabs
          ariaLabel="优惠券编辑页签"
          idPrefix="coupon-form"
          onChange={(id) => setFormTab(id as 'basic' | 'condition')}
          tabs={[{ id: 'basic', label: '基本信息' }, { id: 'condition', label: '使用条件' }]}
          value={formTab}
        />
        <div aria-labelledby={`coupon-form-${formTab}-tab`} className={styles.formContent} id={`coupon-form-${formTab}-panel`} role="tabpanel">
          {formTab === 'basic'
            ? <p className={styles.formPlaceholder}>基本信息字段将在后续版本补充，本次先交付使用条件与叠加生效范围的交互。</p>
            : <>
              <div className={styles.formRow}>
                <div className={`${styles.formLabel} ${styles.required}`}>使用范围：</div>
                <div className={styles.rowValue}>
                  <Checkbox checked={signupScopes.includes('前台报名')} label="前台报名" onCheckedChange={(checked) => setSignupScopes((current) => (checked ? [...current, '前台报名'] : current.filter((item) => item !== '前台报名')))} value="前台报名" />
                  <Checkbox checked={signupScopes.includes('线上报名')} label="线上报名" onCheckedChange={(checked) => setSignupScopes((current) => (checked ? [...current, '线上报名'] : current.filter((item) => item !== '线上报名')))} value="线上报名" />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={`${styles.formLabel} ${styles.required}`}>使用门槛：</div>
                <div className={styles.rowValue}>
                  <Radio checked={thresholdMode === 'none'} label="无门槛" name="threshold" onCheckedChange={() => setThresholdMode('none')} value="none" />
                  <Radio checked={thresholdMode === 'amount'} name="threshold" onCheckedChange={() => setThresholdMode('amount')} value="amount">
                    <>订单满 <span className={styles.amountInput}><InputNumber ariaLabel="订单门槛金额" disabled={thresholdMode !== 'amount'} onValueChange={() => undefined} value={null} /></span> 元可用</>
                  </Radio>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={`${styles.formLabel} ${styles.required}`}>适用于：</div>
                <div className={styles.rowValue}>
                  {applyOptions.map((item) => <Checkbox checked={applyTo.includes(item)} key={item} label={item} onCheckedChange={(checked) => toggleApply(item, checked)} value={item} />)}
                </div>
              </div>
              <div className={styles.formRow}><div className={styles.formLabel}>适用中心：</div><div className={styles.rowValue}><Select ariaLabel="适用中心" className={styles.wideControl} onValueChange={() => undefined} options={centerOptions} value="liuzhi" /></div></div>
              <div className={styles.formRow}><div className={styles.formLabel}>课程：</div><div className={styles.rowValue}><Select ariaLabel="课程" className={styles.wideControl} onValueChange={() => undefined} options={unlimitedOptions} value="unlimited" /></div></div>
              <div className={styles.formRow}><div className={styles.formLabel}>学员类别：</div><div className={styles.rowValue}><Select ariaLabel="学员类别" className={styles.wideControl} onValueChange={() => undefined} options={unlimitedOptions} value="unlimited" /></div></div>
              <div className={`${styles.formRow} ${styles.stackSetting}`}>
                <div className={styles.formLabel}>叠加生效范围：</div>
                <div className={styles.rowValue}>
                {isNew
                  ? <StackRangeField coupon={coupon} entities={entities} groups={groups} onOpenRelation={onOpenRelation} onOpenRules={onOpenRules} unsaved />
                  : <StackRangeField coupon={coupon} entities={entities} groups={groups} onOpenRelation={onOpenRelation} onOpenRules={onOpenRules} />}
                </div>
              </div>
              <div className={styles.formRow}><div className={styles.formLabel}>金额退费返券：</div><div className={styles.rowValue}><Switch checked={refundReturn} label="金额退费返券" onCheckedChange={setRefundReturn} /></div></div>
            </>}
        </div>
      </section>
    </div>
    <div className={styles.editFooter}>
      {isNew
        ? <><Button onClick={onBack} type="default">取消</Button><Button onClick={() => onSave('rules')} type="default">保存并设置叠加规则</Button><Button onClick={() => onSave('back')}>保存</Button></>
        : <><Button onClick={onBack} type="default">返回</Button><Button onClick={() => onSave('back')}>保存</Button></>}
    </div>
  </div>;
}
